import React, { useMemo, useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * Renders an admin-authored category description in a tight, scannable way.
 *
 * Admin descriptions come in two shapes:
 *   (a) Bulleted lists — items separated by "·", "•", "-" or newlines, often
 *       with a "Label: detail" pattern (e.g. "· Warm Welcome: Enthusiastic …").
 *   (b) Free-form prose ("Caseload management, documentation timeliness, …").
 *
 * Strategy:
 *   - Detect list vs prose. For lists, show the first 2 bullets as a real
 *     bulleted list, bold the "Label:" prefix.
 *   - If there are more than 2 bullets (list) OR the prose exceeds ~140 chars,
 *     show a "More" affordance that opens a modal with the full, formatted
 *     description.
 */

const splitToBullets = (raw) => {
  if (!raw) return [];
  let parts = raw.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 1 && /[·•]/.test(raw)) {
    parts = raw.split(/[·•]/).map((s) => s.trim()).filter(Boolean);
  }
  return parts
    .map((p) => p.replace(/^[·•\-*]\s*/, '').trim())
    .filter(Boolean);
};

const BulletItem = ({ children, dotColor = 'text-fuchsia-500' }) => (
  <li className="flex gap-2 leading-snug">
    <span className={`${dotColor} font-bold mt-0.5 flex-shrink-0`}>•</span>
    <span className="min-w-0">{children}</span>
  </li>
);

const renderLabeled = (text) => {
  const m = text.match(/^([^:]{1,40}):\s*(.+)$/);
  if (!m) return text;
  return (
    <>
      <strong className="text-slate-800">{m[1]}:</strong> {m[2]}
    </>
  );
};

export const TruncatedDescription = ({ title, text, className = '' }) => {
  const [open, setOpen] = useState(false);

  const { items, isList, hasMore, prose } = useMemo(() => {
    const bullets = splitToBullets(text || '');
    const list = bullets.length > 1;
    if (list) {
      return {
        items: bullets,
        isList: true,
        hasMore: bullets.length > 2,
        prose: '',
      };
    }
    const p = (text || '').trim();
    return { items: [], isList: false, hasMore: p.length > 140, prose: p };
  }, [text]);

  if (!text) return null;

  const preview = isList ? items.slice(0, 2) : [];

  return (
    <>
      <div className={`text-xs text-slate-600 ${className}`} data-testid="cat-description">
        {isList ? (
          <ul className="space-y-1">
            {preview.map((it, i) => (
              <BulletItem key={i}>
                <span className="text-slate-600">{renderLabeled(it)}</span>
              </BulletItem>
            ))}
          </ul>
        ) : (
          <p className="leading-snug text-slate-600">
            {hasMore ? `${prose.slice(0, 140).trim()}…` : prose}
          </p>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="trunc-more"
            className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold text-fuchsia-600 hover:text-fuchsia-800"
          >
            <Info className="w-3 h-3" />
            {isList ? `View all ${items.length} criteria` : 'Read more'}
          </button>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="trunc-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="trunc-modal"
          >
            <div className="flex items-start justify-between mb-4 gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-fuchsia-600 mb-0.5">
                  Category criteria
                </div>
                <h3 className="text-lg font-bold text-slate-900 leading-tight">{title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="trunc-modal-close"
                className="p-1.5 rounded-lg hover:bg-slate-100 flex-shrink-0"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            {isList ? (
              <ul className="space-y-2.5 text-sm text-slate-700">
                {items.map((it, i) => (
                  <BulletItem key={i}>
                    <span className="text-slate-700">{renderLabeled(it)}</span>
                  </BulletItem>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{text}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TruncatedDescription;
